// PREVIOUS EXPORTED REPORTS - the owner's third tab, 2026-10-22:
// "a tab, to see previous exported reports, with dates created, exported by,
// total cost, and number of issues/shopdrawings, and a detail option to list
// all items".
//
// ITS OWN FILE, DELIBERATELY. ContractorHoursPage is already 700+ lines and
// eslint-plugin-react-hooks miscounts code paths in components that size:
// adding a branchy block to it reports "hook called conditionally" against an
// unrelated earlier hook and FAILS THE BUILD. A tab that owns its own data
// also keeps the page's single report request from being re-run when somebody
// is only reading history.
import { Button, Modal, Table, Tag, Tooltip, message } from "antd";
import { getBillableExport, listBillableExports } from "api/billableHours";
import React, { useCallback, useEffect, useState } from "react";

const money = (n) => (n == null ? null : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const hrs = (n) => `${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
const stamp = (d) => (d ? new Date(d).toLocaleString("en-US", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

// An hours-only report HAS no cost. Showing $0.00 would read as "this work was
// free", which is a different claim from "this document carried no money".
const COST_NONE = <Tooltip title="This report was exported with prices off, so it carries no cost at all - this is not a zero."><span style={{ color: "#999" }}>hours only</span></Tooltip>;

export function ItemsTable({ items, withCost }) {
  return (
    <Table
      rowKey={(r) => `${r.system}:${r.ref}`}
      size="small"
      dataSource={items || []}
      pagination={{ pageSize: 25, size: "small", showSizeChanger: false }}
      locale={{ emptyText: "This report listed no identifiable items." }}
      columns={[
        {
          title: "Item", dataIndex: "code", width: 140,
          render: (v, r) => (v ? <b>{v}</b> : <Tooltip title={`No code recorded; stored by id ${r.ref}`}><span style={{ color: "#999" }}>(no code)</span></Tooltip>),
        },
        { title: "", dataIndex: "system", width: 110, render: (v) => <Tag color={v === "issues" ? "blue" : "geekblue"}>{v === "issues" ? "Issue" : "Shop drawing"}</Tag> },
        { title: "Description", dataIndex: "label", ellipsis: true },
        { title: "Hours", dataIndex: "hours", width: 90, align: "right", render: (v) => hrs(v) },
        ...(withCost ? [{ title: "Cost", dataIndex: "cost", width: 110, align: "right", render: (v) => money(v) || COST_NONE }] : []),
      ]}
    />
  );
}

export default function ReportHistory({ projectId }) {
  const [state, setState] = useState({ loading: true, rows: [], err: "" });
  const [detail, setDetail] = useState(null);
  const [opening, setOpening] = useState("");

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true }));
    listBillableExports({ projectId: projectId || undefined })
      .then((d) => setState({ loading: false, rows: (d && d.rows) || [], err: "" }))
      .catch((e) => setState({ loading: false, rows: [], err: (e && e.message) || "Could not list the exported reports" }));
  }, [projectId]);

  useEffect(load, [load]);

  const open = (id) => {
    setOpening(id);
    getBillableExport(id)
      .then((d) => setDetail(d))
      .catch((e) => message.error((e && e.message) || "Could not open that report"))
      .finally(() => setOpening(""));
  };

  return (
    <>
      <div style={{ color: "#888", fontSize: 13, margin: "4px 0 10px" }}>
        Every contractor hours and cost report that has been exported or emailed from this account, newest first.
        A report is recorded when the document actually went out, so what is listed here is what somebody received.
      </div>
      <Table
        rowKey="_id"
        size="small"
        loading={state.loading}
        dataSource={state.rows}
        locale={{ emptyText: state.err || "No report has been exported from this account yet." }}
        pagination={{ pageSize: 20, size: "small", showSizeChanger: false }}
        columns={[
          { title: "Created", dataIndex: "at", width: 180, render: (v) => stamp(v) },
          { title: "Exported by", dataIndex: "by", width: 170, ellipsis: true, render: (v) => v || <span style={{ color: "#999" }}>(not recorded)</span> },
          {
            title: "Period", dataIndex: "periodLabel", width: 210, ellipsis: true,
            render: (v, r) => (
              <Tooltip title={(r.filter || []).length ? `Filtered to: ${(r.filter || []).join("; ")}` : "No filter beyond the period"}>
                <span>{v || "All dates"}</span>
              </Tooltip>
            ),
          },
          {
            title: "How", dataIndex: "kind", width: 120,
            render: (v, r) => (v === "send"
              ? <Tooltip title={`Emailed to ${(r.recipients || []).length} recipient(s): ${(r.recipients || []).join(", ")}`}><Tag color="purple">emailed</Tag></Tooltip>
              : <Tag>{String(r.format || "").toUpperCase()}</Tag>),
          },
          { title: "Basis", dataIndex: "basis", width: 110, render: (v) => (v === "estimated" ? <Tag color="orange">estimated</Tag> : <Tag color="default">recorded</Tag>) },
          { title: "Issues", dataIndex: "issues", width: 80, align: "right" },
          { title: "Drawings", dataIndex: "shopDrawings", width: 90, align: "right" },
          { title: "Hours", dataIndex: "hours", width: 90, align: "right", render: (v) => hrs(v) },
          { title: "Total cost", dataIndex: "cost", width: 120, align: "right", render: (v) => money(v) || COST_NONE },
          {
            title: "", key: "detail", width: 90,
            render: (_, r) => <Button size="small" loading={opening === r._id} onClick={() => open(r._id)}>Detail</Button>,
          },
        ]}
      />
      <Modal
        open={!!detail}
        onCancel={() => setDetail(null)}
        footer={null}
        width={900}
        title={detail ? `Report of ${stamp(detail.at)} - ${detail.periodLabel || "all dates"}` : ""}
      >
        {detail ? (
          <>
            <div style={{ color: "#888", fontSize: 13, marginBottom: 10 }}>
              Exported by {detail.by || "(not recorded)"}
              {detail.projectTitle ? ` · ${detail.projectTitle}` : ""}
              {" · "}{(detail.counts || {}).issues || 0} issue(s), {(detail.counts || {}).shopDrawings || 0} shop drawing(s)
              {" · "}{hrs((detail.totals || {}).hours)} h
              {detail.withCost ? ` · ${money((detail.totals || {}).cost)}` : " · hours only"}
              {(detail.filter || []).length ? <div>Filtered to: {(detail.filter || []).join("; ")}</div> : null}
              {(detail.recipients || []).length ? <div>Emailed to: {(detail.recipients || []).join(", ")}</div> : null}
            </div>
            <ItemsTable items={detail.items} withCost={detail.withCost} />
          </>
        ) : null}
      </Modal>
    </>
  );
}
